import { useMemo, useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { __ } from '@wordpress/i18n';
import { usePitchField } from './context.js';
import { useSelectionStore } from '@/hooks/useSelection.js';
import { useKeyboardFocusStore } from '@/hooks/useKeyboardFocus.js';
import { useOccupiedStore } from '@/hooks/useOccupied.js';
import Tile from './Tile.jsx';
import BundleTile from './BundleTile.jsx';
import Tooltip from './Tooltip.jsx';
import { useContainerOrientation } from '@/hooks/useContainerOrientation.js';
import { useImageNaturalSize } from '@/hooks/useImageNaturalSize.js';

const IS_DEV = import.meta.env.DEV;

// ─── FocusedAnnouncement ──────────────────────────────────────────────────────
// Defined at module level so it's never recreated on Grid re-renders.
// Subscribes only to the keyboard focus store — doesn't trigger Grid re-renders.

function FocusedAnnouncement( { fieldType, grid, fieldConfig } ) {
	const hasFocus       = useKeyboardFocusStore( ( s ) => s.hasFocus );
	const focusedId      = useKeyboardFocusStore( ( s ) => s.focusedId );
	const focusFieldType = useKeyboardFocusStore( ( s ) => s.fieldType );

	const text = useMemo( () => {
		if ( ! hasFocus || focusFieldType !== fieldType || ! focusedId || ! fieldConfig ) return '';
		const categories = fieldConfig.categories ?? {};
		const bundles    = fieldConfig.bundles    ?? {};
		if ( bundles[ focusedId ] ) return bundles[ focusedId ].label ?? focusedId;
		const tile = grid?.tiles.find( ( t ) => t.id === focusedId );
		if ( ! tile ) return focusedId;
		const cat = categories[ tile.category ] ?? categories[ 'default' ];
		return `${ __( 'Tile', 'web-sponsors-pitch' ) } ${ focusedId } – ${ cat?.label ?? tile.category }`;
	}, [ hasFocus, focusedId, focusFieldType, fieldType, fieldConfig, grid ] );

	return (
		<div className="wsp-sr-only" role="status" aria-live="polite" aria-atomic="true">
			{text}
		</div>
	);
}

// ─── FocusedTooltip ───────────────────────────────────────────────────────────
// Subscribes to focus + selection stores. When focus moves, only this component
// and the two affected Tile/BundleTile components re-render — Grid stays still.

const EMPTY_OBJ = {};

function FocusedTooltip( { fieldType, grid, svgRef, shouldRotate } ) {
	const { fieldConfig } = usePitchField();
	const expandedCache = useOccupiedStore( ( s ) => s.expandedCache[ fieldType ] ?? EMPTY_OBJ );

	const hasFocus       = useKeyboardFocusStore( ( s ) => s.hasFocus );
	const focusedId      = useKeyboardFocusStore( ( s ) => s.focusedId );
	const focusFieldType = useKeyboardFocusStore( ( s ) => s.fieldType );
	const selectionObj   = useSelectionStore( ( s ) => s.selected[ fieldType ] ?? EMPTY_OBJ );

	const isActive = hasFocus && focusFieldType === fieldType && !! focusedId;

	// Screen position — computed mathematically from SVG coordinates.
	// useLayoutEffect runs synchronously before the browser paint, so both renders
	// (null → position) are batched into a single visible frame (no flicker).
	const [ screenPos, setScreenPos ] = useState( null );
	useLayoutEffect( () => {
		if ( ! isActive || ! focusedId || ! grid || ! svgRef?.current ) {
			setScreenPos( null );
			return;
		}

		const tilesForNav = grid.tiles.filter( ( t ) => ( t.bundleId ?? t.id ) === focusedId );
		if ( ! tilesForNav.length ) { setScreenPos( null ); return; }

		// Bounding-box centre in SVG content coordinates
		const xs = tilesForNav.flatMap( ( t ) => [ t.x, t.x + t.w ] );
		const ys = tilesForNav.flatMap( ( t ) => [ t.y, t.y + t.h ] );
		const svgCx = ( Math.min( ...xs ) + Math.max( ...xs ) ) / 2;
		const svgCy = ( Math.min( ...ys ) + Math.max( ...ys ) ) / 2;

		// Map content coords → viewBox coords (accounting for landscape rotation)
		const W       = grid.totalWidth;
		const H       = grid.totalHeight;
		const svgRect = svgRef.current.getBoundingClientRect();

		let x, y;
		if ( shouldRotate ) {
			// Content transform: translate(H,0) rotate(90) → (cx,cy) → (H−cy, cx)
			x = svgRect.left + ( ( H - svgCy ) / H ) * svgRect.width;
			y = svgRect.top  + ( svgCx          / W ) * svgRect.height;
		} else {
			x = svgRect.left + ( svgCx / W ) * svgRect.width;
			y = svgRect.top  + ( svgCy / H ) * svgRect.height;
		}

		setScreenPos( ( prev ) => ( prev && prev.x === x && prev.y === y ? prev : { x, y } ) );
	}, [ isActive, focusedId, grid, svgRef, shouldRotate ] );

	const tooltipData = useMemo( () => {
		if ( ! isActive || ! focusedId || ! grid ) return null;

		const tilesForNav = grid.tiles.filter( ( t ) => ( t.bundleId ?? t.id ) === focusedId );
		if ( ! tilesForNav.length ) return null;

		const isBundle = !! tilesForNav[ 0 ]?.bundleId;
		if ( isBundle ) {
			const bundleTileIds = tilesForNav.map( ( t ) => t.id );
			// Use O(1) store lookups instead of array filter
			let occEntry = expandedCache[ '#' + focusedId ] ?? null;
			if ( ! occEntry ) {
				for ( const id of bundleTileIds ) {
					const e = expandedCache[ id ];
					if ( ! e ) continue;
					if ( e.status === 'publish' ) { occEntry = e; break; }
					if ( ! occEntry ) occEntry = e;
				}
			}
			const isSel = bundleTileIds.some( ( id ) => !! selectionObj[ id ] );
			const isOcc = !! occEntry;
			const isPub = occEntry?.status === 'publish';
			const state = isOcc ? ( isPub ? 'occupied' : 'pending' ) : isSel ? 'selected' : 'free';
			return {
				state,
				tile: {
					bundleId:  focusedId,
					count:     tilesForNav.length,
					totalArea: tilesForNav.reduce( ( s, t ) => s + t.w * t.h, 0 ),
					category:  tilesForNav[ 0 ]?.category ?? 'default',
				},
				occupiedData: occEntry,
			};
		}

		const tileObj  = tilesForNav[ 0 ];
		const occEntry = expandedCache[ tileObj.id ] ?? null;
		const isSel    = !! selectionObj[ tileObj.id ];
		const isOcc    = !! occEntry;
		const state    = isOcc
			? ( occEntry.status === 'publish' ? 'occupied' : 'pending' )
			: isSel ? 'selected' : 'free';
		return { state, tile: tileObj, occupiedData: occEntry };
	}, [ isActive, focusedId, grid, expandedCache, selectionObj ] );

	if ( ! tooltipData || ! screenPos ) return null;

	return (
		<Tooltip
			visible={true}
			mouseX={screenPos.x}
			mouseY={screenPos.y}
			state={tooltipData.state}
			tile={tooltipData.tile}
			fieldConfig={fieldConfig}
			occupiedData={tooltipData.occupiedData}
		/>
	);
}

// ─── HoverTooltip ─────────────────────────────────────────────────────────────
// Listens for pointermove/pointerleave on the grid wrapper via a DOM event
// listener (not React synthetic events on tiles).  Individual tiles have zero
// hover state — CSS :hover handles the visual fill entirely; this component
// handles only the informational tooltip.
//
// Mouse-position is stored in a ref so pointer-move does NOT cause re-renders.
// A state update only fires when the hovered navId changes (i.e. the pointer
// moves from one tile to another), so the tooltip re-renders ~once per tile
// entry rather than on every pixel of movement.

function HoverTooltip( { fieldType, grid, wrapRef } ) {
	const { fieldConfig } = usePitchField();
	const expandedCache  = useOccupiedStore( ( s ) => s.expandedCache[ fieldType ] ?? EMPTY_OBJ );
	const selectionObj = useSelectionStore( ( s ) => s.selected[ fieldType ] ?? EMPTY_OBJ );

	const [ hoveredNavId, setHoveredNavId ] = useState( null );
	const posRef = useRef( { x: 0, y: 0 } );

	useEffect( () => {
		const el = wrapRef?.current;
		if ( ! el ) return;

		function onPointerMove( e ) {
			posRef.current = { x: e.clientX, y: e.clientY };
			const target = document.elementFromPoint( e.clientX, e.clientY );
			const g      = target?.closest( '[data-nav-id]' );
			const navId  = g?.dataset?.navId ?? null;
			setHoveredNavId( ( prev ) => ( prev === navId ? prev : navId ) );
		}

		function onPointerLeave() {
			setHoveredNavId( null );
		}

		el.addEventListener( 'pointermove',  onPointerMove,  { passive: true } );
		el.addEventListener( 'pointerleave', onPointerLeave );
		return () => {
			el.removeEventListener( 'pointermove',  onPointerMove );
			el.removeEventListener( 'pointerleave', onPointerLeave );
		};
	}, [ wrapRef ] );

	const tooltipData = useMemo( () => {
		if ( ! hoveredNavId || ! grid ) return null;

		const tilesForNav = grid.tiles.filter( ( t ) => ( t.bundleId ?? t.id ) === hoveredNavId );
		if ( ! tilesForNav.length ) return null;

		const isBundle = !! tilesForNav[ 0 ]?.bundleId;

		if ( isBundle ) {
			const bundleTileIds = tilesForNav.map( ( t ) => t.id );
			let occEntry = expandedCache[ '#' + hoveredNavId ] ?? null;
			if ( ! occEntry ) {
				for ( const id of bundleTileIds ) {
					const e = expandedCache[ id ];
					if ( ! e ) continue;
					if ( e.status === 'publish' ) { occEntry = e; break; }
					if ( ! occEntry ) occEntry = e;
				}
			}
			const isSel = bundleTileIds.some( ( id ) => !! selectionObj[ id ] );
			const isOcc = !! occEntry;
			const isPub = occEntry?.status === 'publish';
			const state = isOcc ? ( isPub ? 'occupied' : 'pending' ) : isSel ? 'selected' : 'free';
			return {
				state,
				tile: {
					bundleId:  hoveredNavId,
					count:     tilesForNav.length,
					totalArea: tilesForNav.reduce( ( s, t ) => s + t.w * t.h, 0 ),
					category:  tilesForNav[ 0 ]?.category ?? 'default',
				},
				occupiedData: occEntry,
			};
		}

		const tileObj  = tilesForNav[ 0 ];
		const occEntry = expandedCache[ tileObj.id ] ?? null;
		const isSel    = !! selectionObj[ tileObj.id ];
		const isOcc    = !! occEntry;
		const state    = isOcc
			? ( occEntry.status === 'publish' ? 'occupied' : 'pending' )
			: isSel ? 'selected' : 'free';
		return { state, tile: tileObj, occupiedData: occEntry };
	}, [ hoveredNavId, grid, expandedCache, selectionObj ] );

	if ( ! tooltipData ) return null;

	return (
		<Tooltip
			visible={true}
			mouseX={posRef.current.x}
			mouseY={posRef.current.y}
			state={tooltipData.state}
			tile={tooltipData.tile}
			fieldConfig={fieldConfig}
			occupiedData={tooltipData.occupiedData}
		/>
	);
}

// ─── Grid ─────────────────────────────────────────────────────────────────────

/**
 * Renders the SVG grid overlaid on the background image.
 *
 * Performance notes:
 *  - focusedId / hasFocus live in useKeyboardFocusStore, NOT in Grid React state.
 *    Arrow keys call store.setFocusedId() directly — Grid itself does NOT re-render
 *    on every keypress. Only the two affected tiles and FocusedTooltip re-render.
 *  - Tile selection is stored as Record<string,true> for O(1) per-tile lookups.
 *  - Drag-select applies selectMany / deselectMany in single batched store updates.
 *  - grid is computed once in index.jsx (PitchField root) and passed via context —
 *    no second buildGrid() call here.
 *  - Pointer events (onPointerDown/Enter/Leave/Move) replace mouse-only events
 *    so touch-based drag and hover work on mobile/tablet.
 */
export default function Grid() {
	const { fieldConfig, grid, normalizedBundles, toggleTile, mode, fieldType } = usePitchField();
	const { ref, isLandscape } = useContainerOrientation( 480 );

	const [ showLabels, setShowLabels ] = useState( () => {
		if ( ! IS_DEV ) return false;
		try { return localStorage.getItem( 'wsp-show-labels' ) === 'true'; } catch { return false; }
	} );
	useEffect( () => {
		if ( ! IS_DEV ) return;
		try { localStorage.setItem( 'wsp-show-labels', String( showLabels ) ); } catch { /* ignore */ }
	}, [ showLabels ] );

	// Ref on the SVG element — used by FocusedTooltip for coordinate conversion
	const svgRef               = useRef( null );
	// Drag-select state (pointer)
	const dragRef              = useRef( null );
	// Shift+Arrow sequence: mode is fixed by the anchor tile on first press
	const shiftDragRef         = useRef( null ); // null | { mode: 'add'|'remove' }
	// Prevents pointer click from activating keyboard focus highlight
	const mouseInitiatedFocusRef = useRef( false );

	// End drag on pointerup anywhere on the page (covers mouse + touch + stylus)
	useEffect( () => {
		function onPointerUp() { dragRef.current = null; }
		window.addEventListener( 'pointerup', onPointerUp );
		return () => window.removeEventListener( 'pointerup', onPointerUp );
	}, [] );

	// ── Navigation map ────────────────────────────────────────────────────────
	const navMap = useMemo( () => {
		if ( ! grid ) return null;
		const { tiles, cols, rows } = grid;

		const posToNav       = new Map();
		const navBounds      = new Map();
		const navToToggleTile = new Map();

		for ( const tile of tiles ) {
			if ( tile.disabled ) continue;
			const navId = tile.bundleId ?? tile.id;
			posToNav.set( `${ tile.col },${ tile.row }`, navId );

			if ( ! navToToggleTile.has( navId ) ) {
				navToToggleTile.set( navId, tile.id );
			}

			if ( ! navBounds.has( navId ) ) {
				navBounds.set( navId, { minCol: tile.col, maxCol: tile.col, minRow: tile.row, maxRow: tile.row } );
			} else {
				const b = navBounds.get( navId );
				if ( tile.col < b.minCol ) b.minCol = tile.col;
				if ( tile.col > b.maxCol ) b.maxCol = tile.col;
				if ( tile.row < b.minRow ) b.minRow = tile.row;
				if ( tile.row > b.maxRow ) b.maxRow = tile.row;
			}
		}

		let firstId = null; // portrait top-left
		let lastId  = null; // portrait bottom-right
		for ( const tile of tiles ) {
			if ( ! tile.disabled ) {
				const navId = tile.bundleId ?? tile.id;
				if ( ! firstId ) firstId = navId;
				lastId = navId;
			}
		}

		/**
		 * Walks a straight line (fixed column OR fixed row) in (dCol,dRow) direction
		 * from navId to the grid edge.  Returns an ordered array of unique navIds
		 * encountered along the line (start tile excluded).
		 *
		 * Unlike the chained navigate() approach this NEVER drifts sideways through
		 * bundle centres — it always follows exactly one column or one row.
		 *
		 * The "fixed" coordinate is the centre of the starting tile:
		 *   • vertical move   (dRow≠0): fixed column = round((minCol+maxCol)/2)
		 *   • horizontal move (dCol≠0): fixed row    = round((minRow+maxRow)/2)
		 *
		 * Any navId that has at least one tile intersecting that column/row and lies
		 * in the direction of movement is included — even large bundles.
		 */
		function navigateToEdgeStraight( navId, dCol, dRow ) {
			const b = navBounds.get( navId );
			if ( ! b ) return [];

			const isColDir  = dCol !== 0;
			const dDir      = isColDir ? dCol : dRow;
			const axisLimit = isColDir ? cols : rows;

			// Pin perpendicular coord to the centre of the starting tile
			const fixedPerp = isColDir
				? Math.round( ( b.minRow + b.maxRow ) / 2 )
				: Math.round( ( b.minCol + b.maxCol ) / 2 );

			// First position to check: one step past the leading edge
			const startAxis = (
				isColDir
					? ( dCol > 0 ? b.maxCol : b.minCol )
					: ( dRow > 0 ? b.maxRow : b.minRow )
			) + dDir;

			const path = [];
			const seen = new Set( [ navId ] );

			for ( let axisPos = startAxis; axisPos >= 0 && axisPos < axisLimit; axisPos += dDir ) {
				const c  = isColDir ? axisPos : fixedPerp;
				const r  = isColDir ? fixedPerp : axisPos;
				const id = posToNav.get( `${ c },${ r }` );
				if ( id !== undefined && ! seen.has( id ) ) {
					seen.add( id );
					path.push( id );
				}
			}
			return path;
		}

		function navigate( navId, dCol, dRow ) {
			const b = navBounds.get( navId );
			if ( ! b ) return null;

			const isColDir      = dCol !== 0;
			const dDir          = isColDir ? dCol : dRow;
			const startBoundary = isColDir
				? ( dCol > 0 ? b.maxCol : b.minCol )
				: ( dRow > 0 ? b.maxRow : b.minRow );
			const perpMin    = isColDir ? b.minRow : b.minCol;
			const perpMax    = isColDir ? b.maxRow : b.maxCol;
			const perpCenter = ( perpMin + perpMax ) / 2;
			const axisLimit  = isColDir ? cols : rows;

			for ( let distance = 0; ; distance++ ) {
				const axisPos = startBoundary + distance * dDir;
				if ( axisPos < 0 || axisPos >= axisLimit ) break;

				const candidates = new Map();
				for ( let perp = perpMin; perp <= perpMax; perp++ ) {
					const c  = isColDir ? axisPos : perp;
					const r  = isColDir ? perp    : axisPos;
					const id = posToNav.get( `${ c },${ r }` );
					if ( id !== undefined && id !== navId ) {
						const dist = Math.abs( perp - perpCenter );
						if ( ! candidates.has( id ) || dist < candidates.get( id ) ) candidates.set( id, dist );
					}
				}

				if ( candidates.size > 0 ) {
					let best = null, bestDist = Infinity;
					for ( const [ id, dist ] of candidates ) {
						if ( dist < bestDist ) { bestDist = dist; best = id; }
					}
					return best;
				}
			}
			return null;
		}

		return { posToNav, navBounds, navigate, navigateToEdgeStraight, firstId, lastId, navToToggleTile };
	}, [ grid ] );

	// ── Tile partitioning ─────────────────────────────────────────────────────
	const { bundleGroups, standaloneTiles } = useMemo( () => {
		if ( ! grid ) return { bundleGroups: {}, standaloneTiles: [] };

		const bundles = normalizedBundles ?? [];
		const groups  = {};
		const solo    = [];

		for ( const tile of grid.tiles ) {
			if ( tile.disabled ) continue;
			if ( tile.bundleId ) {
				const bundle = bundles.find( ( b ) => b.id === tile.bundleId );
				if ( bundle ) {
					if ( ! groups[ tile.bundleId ] ) groups[ tile.bundleId ] = { bundle, tiles: [] };
					groups[ tile.bundleId ].tiles.push( tile );
					continue;
				}
			}
			solo.push( tile );
		}
		return { bundleGroups: groups, standaloneTiles: solo };
	}, [ grid, normalizedBundles ] );

	const imgUrl = fieldConfig?.background_image_url ?? null;
	const { width: imgNaturalW, height: imgNaturalH } = useImageNaturalSize( imgUrl );

	// ── Layout / rotation ─────────────────────────────────────────────────────
	const { totalWidth: W, totalHeight: H } = grid ?? { totalWidth: 1, totalHeight: 1 };
	const shouldRotate       = isLandscape && H > W;
	const portraitImgRatio   = imgNaturalW && imgNaturalH ? imgNaturalW / imgNaturalH : W / H;
	const displayAspectRatio = shouldRotate ? 1 / portraitImgRatio : portraitImgRatio;
	const viewBox            = shouldRotate ? `0 0 ${ H } ${ W }` : `0 0 ${ W } ${ H }`;
	const contentTransform   = shouldRotate ? `translate(${ H }, 0) rotate(90)` : undefined;
	const svgStyle           = { display: 'block', width: '100%', aspectRatio: displayAspectRatio };
	const labelFontSize      = grid
		? Math.min( grid.gridWidth / grid.cols, grid.gridHeight / grid.rows ) * 0.28
		: 1;

	// ── Drag-select callbacks ─────────────────────────────────────────────────
	const handleTilePointerDown = useCallback( ( navId, tileIds, isOccupied ) => {
		if ( mode !== 'interactive' || isOccupied ) return;
		const sel      = useSelectionStore.getState().selected[ fieldType ] ?? {};
		const dragMode = tileIds.every( ( id ) => !! sel[ id ] ) ? 'remove' : 'add';
		dragRef.current = { mode: dragMode, touched: new Set( [ navId ] ) };
		const store = useSelectionStore.getState();
		if ( dragMode === 'add' ) store.selectMany( fieldType, tileIds );
		else                      store.deselectMany( fieldType, tileIds );
	}, [ mode, fieldType ] );

	const handleTileDragEnter = useCallback( ( navId, tileIds, isOccupied ) => {
		if ( ! dragRef.current || isOccupied ) return;
		if ( dragRef.current.touched.has( navId ) ) return;
		dragRef.current.touched.add( navId );
		const store = useSelectionStore.getState();
		if ( dragRef.current.mode === 'add' ) store.selectMany( fieldType, tileIds );
		else                                  store.deselectMany( fieldType, tileIds );
	}, [ fieldType ] );

	// Wrapper-level pointer move: handles drag-select for touch/stylus where
	// pointerenter doesn't fire on other elements during a held gesture.
	// (Also acts as a fallback for mouse drag when pointerenter is missed.)
	function handleWrapperPointerMove( e ) {
		if ( ! dragRef.current || ! grid ) return;
		const el = document.elementFromPoint( e.clientX, e.clientY );
		if ( ! el ) return;
		const g = el.closest( '[data-nav-id]' );
		if ( ! g ) return;
		const navId  = g.dataset.navId;
		if ( dragRef.current.touched.has( navId ) ) return;
		const tileIds = grid.tiles
			.filter( ( t ) => ( t.bundleId ?? t.id ) === navId )
			.map( ( t ) => t.id );
		const occMap = useOccupiedStore.getState().expandedCache[ fieldType ] ?? {};
		const isOcc  = tileIds.some( ( id ) => !! occMap[ id ] ) || !! occMap[ '#' + navId ];
		handleTileDragEnter( navId, tileIds, isOcc );
	}

	// ── Keyboard handlers ─────────────────────────────────────────────────────
	const arrowToDir = useCallback( ( key ) => {
		if ( shouldRotate ) {
			if ( key === 'ArrowRight' ) return [ 0, -1 ];
			if ( key === 'ArrowLeft'  ) return [ 0,  1 ];
			if ( key === 'ArrowDown'  ) return [ 1,  0 ];
			if ( key === 'ArrowUp'    ) return [ -1, 0 ];
		} else {
			if ( key === 'ArrowRight' ) return [ 1,  0 ];
			if ( key === 'ArrowLeft'  ) return [ -1, 0 ];
			if ( key === 'ArrowDown'  ) return [ 0,  1 ];
			if ( key === 'ArrowUp'    ) return [ 0, -1 ];
		}
		return null;
	}, [ shouldRotate ] );

	function handleKeyDown( e ) {
		if ( ! navMap ) return;

		// Reset shift-drag mode as soon as Shift is no longer held
		if ( ! e.shiftKey ) shiftDragRef.current = null;

		// Read focus from store — no Grid state needed
		const focusState = useKeyboardFocusStore.getState();
		const focusedId  = focusState.fieldType === fieldType ? focusState.focusedId : null;

		// Home → visual top-left, End → visual bottom-right (rotation-aware)
		if ( e.key === 'Home' || e.key === 'End' ) {
			e.preventDefault();
			shiftDragRef.current = null;
			const target = e.key === 'Home' ? getVisualFirstId() : getVisualLastId();
			if ( target ) useKeyboardFocusStore.getState().setFocusedId( fieldType, target );
			return;
		}

		if ( e.key === 'Enter' || e.key === ' ' ) {
			e.preventDefault();
			const current = focusedId ?? navMap.firstId;
			if ( current && mode === 'interactive' ) {
				const tileId = navMap.navToToggleTile.get( current );
				if ( tileId && grid ) {
					const occMap     = useOccupiedStore.getState().expandedCache[ fieldType ] ?? {};
					const navTileIds = grid.tiles
						.filter( ( t ) => ( t.bundleId ?? t.id ) === current )
						.map( ( t ) => t.id );
					const isOcc = navTileIds.some( ( id ) => !! occMap[ id ] ) || !! occMap[ '#' + current ];
					if ( ! isOcc ) toggleTile( tileId );
				}
			}
			return;
		}

		const dir = arrowToDir( e.key );
		if ( ! dir ) return;

		e.preventDefault();
		const [ dCol, dRow ] = dir;
		const current = focusedId ?? navMap.firstId;
		if ( ! current ) return;

		// ── Ctrl+Arrow — jump to the start/end of the row or column ──────────────
		if ( e.ctrlKey || e.metaKey ) {
			const path = navMap.navigateToEdgeStraight( current, dCol, dRow );
			if ( ! path.length ) return; // already at the edge

			const destination = path[ path.length - 1 ];
			useKeyboardFocusStore.getState().setFocusedId( fieldType, destination );

			// Shift+Ctrl+Arrow — apply the same add/remove mode to every tile along the path
			if ( e.shiftKey && mode === 'interactive' && grid ) {
				// Determine mode from anchor tile (same rule as Shift+Arrow)
				if ( ! shiftDragRef.current ) {
					const sel            = useSelectionStore.getState().selected[ fieldType ] ?? {};
					const anchorTileIds  = grid.tiles
						.filter( ( t ) => ( t.bundleId ?? t.id ) === current )
						.map( ( t ) => t.id );
					const anchorSelected = anchorTileIds.every( ( id ) => !! sel[ id ] );
					shiftDragRef.current = { mode: anchorSelected ? 'add' : 'remove' };
				}
				const store  = useSelectionStore.getState();
				const occMap = useOccupiedStore.getState().expandedCache;
				for ( const navId of path ) {
					const navTileIds = grid.tiles
						.filter( ( t ) => ( t.bundleId ?? t.id ) === navId )
						.map( ( t ) => t.id );
					const isOcc = navTileIds.some( ( id ) => !! occMap[ id ] ) || !! occMap[ '#' + navId ];
					if ( isOcc ) continue;
					if ( shiftDragRef.current.mode === 'add' ) store.selectMany( fieldType, navTileIds );
					else                                        store.deselectMany( fieldType, navTileIds );
				}
			}
			return;
		}

		// ── Single-step Arrow navigation ──────────────────────────────────────────
		const next = navMap.navigate( current, dCol, dRow );
		if ( next ) {
			useKeyboardFocusStore.getState().setFocusedId( fieldType, next );

			if ( e.shiftKey && mode === 'interactive' && grid ) {
				const occMap     = useOccupiedStore.getState().expandedCache[ fieldType ] ?? {};
				const navTileIds = grid.tiles
					.filter( ( t ) => ( t.bundleId ?? t.id ) === next )
					.map( ( t ) => t.id );
				const isOcc = navTileIds.some( ( id ) => !! occMap[ id ] ) || !! occMap[ '#' + next ];
				if ( ! isOcc ) {
					// On the first Shift+Arrow, fix the mode from the ANCHOR tile
					// (same behaviour as pointer drag: start tile determines add/remove).
					if ( ! shiftDragRef.current ) {
						const sel            = useSelectionStore.getState().selected[ fieldType ] ?? {};
						const anchorTileIds  = grid.tiles
							.filter( ( t ) => ( t.bundleId ?? t.id ) === current )
							.map( ( t ) => t.id );
						const anchorSelected = anchorTileIds.every( ( id ) => !! sel[ id ] );
						shiftDragRef.current = { mode: anchorSelected ? 'add' : 'remove' };
					}
					const store = useSelectionStore.getState();
					if ( shiftDragRef.current.mode === 'add' ) store.selectMany( fieldType, navTileIds );
					else                                        store.deselectMany( fieldType, navTileIds );
				}
			}
		}
	}

	// ── Visual-edge helpers ──────────────────────────────────────────────────────
	// Returns the navId of the visual top-left / bottom-right tile, taking the
	// 90° SVG rotation into account.  Used by Home/End AND the initial focus.
	//
	// Rotation mapping (transform="translate(H,0) rotate(90)"):
	//   portrait (col=0, row=rows-1)  →  landscape visual top-left
	//   portrait (col=cols-1, row=0)  →  landscape visual bottom-right

	function getVisualFirstId() {
		if ( ! navMap ) return null;
		if ( shouldRotate && grid ) {
			const { cols: gridCols, rows: gridRows } = grid;
			const { posToNav } = navMap;
			for ( let row = gridRows - 1; row >= 0; row-- ) {
				const id = posToNav.get( `0,${ row }` );
				if ( id ) return id;
			}
			return null;
		}
		return navMap.firstId;
	}

	function getVisualLastId() {
		if ( ! navMap ) return null;
		if ( shouldRotate && grid ) {
			const { cols: gridCols, rows: gridRows } = grid;
			const { posToNav } = navMap;
			for ( let row = 0; row < gridRows; row++ ) {
				const id = posToNav.get( `${ gridCols - 1 },${ row }` );
				if ( id ) return id;
			}
			return null;
		}
		return navMap.lastId;
	}

	function handleContainerPointerDown() {
		mouseInitiatedFocusRef.current = true;
		useKeyboardFocusStore.getState().setHasFocus( false );
	}

	function handleFocus() {
		if ( mouseInitiatedFocusRef.current ) {
			mouseInitiatedFocusRef.current = false;
			return;
		}
		const store = useKeyboardFocusStore.getState();
		if ( store.fieldType !== fieldType || ! store.focusedId ) {
			store.setFocusedId( fieldType, getVisualFirstId() ?? null );
		} else {
			store.setHasFocus( true );
		}
	}

	function handleBlur() {
		useKeyboardFocusStore.getState().setHasFocus( false );
	}

	if ( ! fieldConfig || ! grid ) {
		return <div ref={ref} className="wsp-pitch-grid-wrap" />;
	}

	return (
		<div
			ref={ref}
			className="wsp-pitch-grid-wrap"
			tabIndex={0}
			role="application"
			aria-label={fieldConfig?.title ?? __( 'Pitch', 'web-sponsors-pitch' )}
			onPointerDown={handleContainerPointerDown}
			onPointerMove={handleWrapperPointerMove}
			onKeyDown={handleKeyDown}
			onFocus={handleFocus}
			onBlur={handleBlur}
		>
			{/* SR live region — in its own component so Grid doesn't re-render on focus changes */}
			<FocusedAnnouncement fieldType={fieldType} grid={grid} fieldConfig={fieldConfig} />

			<svg
				ref={svgRef}
				viewBox={viewBox}
				preserveAspectRatio="none"
				className="wsp-pitch-grid"
				aria-hidden="true"
				style={svgStyle}
			>
				<g transform={contentTransform}>
					{imgUrl && (
						<image
							href={imgUrl}
							x={0}
							y={0}
							width={W}
							height={H}
							preserveAspectRatio="none"
							className="wsp-pitch-grid__bg"
						/>
					)}

					<g className="wsp-pitch-grid__tiles">
						{standaloneTiles.map( ( tile ) => (
							<Tile
								key={tile.id}
								tile={tile}
								onTilePointerDown={handleTilePointerDown}
								onTileDragEnter={handleTileDragEnter}
							/>
						) )}
					</g>

					<g className="wsp-pitch-grid__bundles">
						{Object.values( bundleGroups ).map( ( { bundle, tiles } ) => (
							<BundleTile
								key={bundle.id}
								bundleId={bundle.id}
								tiles={tiles}
								onTilePointerDown={handleTilePointerDown}
								onTileDragEnter={handleTileDragEnter}
							/>
						) )}
					</g>

					{IS_DEV && showLabels && (
						<g className="wsp-pitch-grid__debug-labels" aria-hidden="true">
							{grid.tiles.map( ( tile ) => {
								const cx = tile.x + tile.w / 2;
								const cy = tile.y + tile.h / 2;
								return (
									<text
										key={tile.id}
										x={cx}
										y={cy}
										textAnchor="middle"
										dominantBaseline="central"
										fontSize={labelFontSize}
										transform={shouldRotate ? `rotate(-90, ${ cx }, ${ cy })` : undefined}
										className="wsp-pitch-grid__debug-label"
										style={{ pointerEvents: 'none' }}
									>
										{tile.id}
									</text>
								);
							} )}
						</g>
					)}
				</g>
			</svg>

			{/* Keyboard-focus tooltip — in its own component so Grid doesn't re-render */}
			<FocusedTooltip fieldType={fieldType} grid={grid} svgRef={svgRef} shouldRotate={shouldRotate} />

			{/* Hover tooltip — driven by pointermove on the wrapper, zero JS in tiles */}
			<HoverTooltip fieldType={fieldType} grid={grid} wrapRef={ref} />

			{IS_DEV && (
				<button
					type="button"
					className={`wsp-dev-label-toggle${ showLabels ? ' wsp-dev-label-toggle--active' : '' }`}
					onClick={() => setShowLabels( ( v ) => ! v )}
					title="Feldnamen ein-/ausblenden"
				>
					<span className="wsp-dev-label-toggle__icon">⊞</span>
					Feldnamen
				</button>
			)}
		</div>
	);
}
